import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { AdminPageHeader, Card, Input, Textarea, Btn, Spinner, ErrorBanner } from "./_shared";
import { Save } from "lucide-react";

const FIELDS = [
  { key: "homepage_hero_eyebrow", label: "Homepage eyebrow", type: "text" },
  { key: "homepage_hero_title_1", label: "Homepage title — line 1", type: "text" },
  { key: "homepage_hero_title_2", label: "Homepage title — line 2", type: "text" },
  { key: "homepage_hero_sub", label: "Homepage sub-text", type: "textarea" },
  { key: "announcement", label: "Site-wide announcement banner", type: "textarea" },
  { key: "share_template_default", label: "Default share text template", type: "textarea" },
  { key: "og_title", label: "OG title (social previews)", type: "text" },
  { key: "og_description", label: "OG description (social previews)", type: "textarea" },
];

export const Settings = () => {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/settings").then((r) => setS(r.data || {})).catch((e) => setErr(e?.response?.data?.detail || "Failed"));
  }, []);

  const set = (k, v) => setS((cur) => ({ ...cur, [k]: v }));

  const save = async () => {
    setSaving(true); setFlash(null);
    try {
      const payload = {};
      FIELDS.forEach((f) => { payload[f.key] = s[f.key] ?? ""; });
      payload.profanity_filter = !!s.profanity_filter;
      payload.moderation_required = !!s.moderation_required;
      const r = await adminApi.put("/admin/settings", payload);
      setS(r.data);
      setFlash("Saved.");
      setTimeout(() => setFlash(null), 2500);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (err && !s) return <ErrorBanner message={err} />;
  if (!s) return <Spinner />;

  return (
    <div className="space-y-5" data-testid="settings-page">
      <AdminPageHeader eyebrow="SETTINGS" title="Site configuration" sub="Editable defaults the public homepage and share tools read from." />

      {err && <ErrorBanner message={err} />}
      {flash && (
        <div className="text-xs uppercase font-extrabold tracking-[0.3em] px-3 py-2 border-2" style={{ background: "#0a2a0a", borderColor: "#138808", color: "#9be09b" }}>
          {flash}
        </div>
      )}

      <Card title="Public copy">
        <div className="space-y-4">
          {FIELDS.map((f) => (
            <Field key={f.key} label={f.label}>
              {f.type === "textarea" ? (
                <Textarea rows={3} value={s[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`settings-${f.key}`} />
              ) : (
                <Input value={s[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`settings-${f.key}`} />
              )}
            </Field>
          ))}
        </div>
      </Card>

      <Card title="Moderation">
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={!!s.profanity_filter}
              onChange={(e) => set("profanity_filter", e.target.checked)}
              data-testid="settings-profanity"
            />
            <span className="uppercase font-bold tracking-[0.2em] text-xs">Profanity word-list filter</span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={!!s.moderation_required}
              onChange={(e) => set("moderation_required", e.target.checked)}
              data-testid="settings-mod-required"
            />
            <span className="uppercase font-bold tracking-[0.2em] text-xs">Require manual approval before publishing</span>
          </label>
        </div>
      </Card>

      <div>
        <Btn onClick={save} disabled={saving} data-testid="settings-save">
          <Save size={14} strokeWidth={3} /> {saving ? "Saving…" : "Save settings"}
        </Btn>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[10px] uppercase font-extrabold tracking-[0.3em] text-neutral-400 mb-2">{label}</span>
    {children}
  </label>
);

export default Settings;
